import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import ContactForm from '../components/ContactForm';
import { COMPANY, IMAGES, MOLD_STEPS } from '../data/mock';

const MoldInspection = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Mold Inspection',
    provider: { '@type': 'LocalBusiness', name: '360 Mold Services', telephone: '+1-226-972-6440' },
    areaServed: 'Southern Ontario',
    description: 'Certified mold inspection using thermal imaging, moisture mapping and accredited lab testing.',
  };
  return (
    <div>
      <Seo
        path="/mold-inspection"
        title="Mold Inspection Services"
        description="Certified mold inspection across Southern Ontario — thermal imaging, moisture mapping and accredited lab sampling, with a clear report in about a week. We test, never remediate."
        schema={schema}
      />
      <section className="brand-gradient relative overflow-hidden">
        <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-[#c7e79b]/40 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="text-brand font-semibold text-sm uppercase tracking-wider">Mold Inspection Services</span>
            <h1 className="mt-3 text-4xl lg:text-5xl font-extrabold text-[#13251b] leading-tight">Find the mold. Find the source. Fix the right thing.</h1>
            <p className="mt-5 text-lg text-[#44564b] leading-relaxed">Mold and moisture problems don’t fix themselves. Using flashlights, infrared thermal imaging, moisture meters and lab sampling, we identify the extent of any issue — and exactly how to address it.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild className="bg-brand hover:bg-[#5c9a22] text-white rounded-full px-7 h-12 font-semibold">
                <Link to="/contact">Book Inspection <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full px-7 h-12 font-semibold border-[#cbdcbb] text-[#13251b] hover:bg-white">
                <a href={COMPANY.phoneHref}><Phone className="h-4 w-4 mr-1" /> {COMPANY.phone}</a>
              </Button>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <img src={IMAGES.moldInspection} alt="Mold inspection" className="rounded-[1.75rem] w-full h-[400px] object-cover shadow-xl" />
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl mx-auto text-center">
            <span className="text-brand font-semibold text-sm uppercase tracking-wider">What’s Included</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#13251b]">A complete, certified assessment</h2>
            <p className="mt-4 text-[#5b6a60]">Every inspection follows a proven, standards-based process from first look to final report.</p>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {MOLD_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={(i % 4) * 80} className="bg-[#fafcf7] rounded-2xl p-6 border border-[#eef2e8] hover-lift">
                  <div className="h-12 w-12 rounded-xl bg-[#eaf4dd] flex items-center justify-center">
                    <Icon className="h-6 w-6 text-brand" />
                  </div>
                  <h3 className="mt-4 font-semibold text-[#13251b]">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#5b6a60] leading-relaxed">{s.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 dark-section text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <Reveal>
            <h2 className="text-3xl font-bold">Why only testing — never remediation?</h2>
            <p className="mt-4 text-[#c3d2c6] leading-relaxed">Because it keeps us honest. We prepare a customized report of our findings and, if needed, a scope of work for a certified remediation contractor. Your interests always come first.</p>
          </Reveal>
          <Reveal delay={120} className="space-y-3">
            {['Customized, easy-to-understand reports','Interpretation of independent lab results','Written strategies &amp; preventative measures','Post-remediation clearance testing available'].map((t) => (
              <div key={t} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                <CheckCircle2 className="h-5 w-5 text-[#8bd44a] shrink-0 mt-0.5" />
                <span className="text-[#e2ebe4]" dangerouslySetInnerHTML={{ __html: t }} />
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-[#fafcf7]">
        <div className="max-w-2xl mx-auto px-5 lg:px-8">
          <Reveal className="bg-white rounded-[1.5rem] p-7 lg:p-8 shadow-lg border border-[#eef2e8]">
            <h3 className="text-2xl font-bold text-[#13251b]">Request a mold inspection quote</h3>
            <p className="mt-1 text-sm text-[#5b6a60] mb-6">We’ll walk you through the process over the phone or get you booked.</p>
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default MoldInspection;
